import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import {
  Check,
  X,
  MessageSquare,
  Users,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  Send,
  ChevronRight,
  FileText,
  ArrowLeft,
  Phone,
  Edit3,
  Calendar,
  Loader2,
  Shield,
  History,
  User
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Approval Step Card
const ApprovalStepCard = ({ step, isActive, isCompleted, children }) => {
  return (
    <Card className={`transition-all ${
      isActive ? 'border-violet-500 shadow-lg shadow-violet-500/20' : 
      isCompleted ? 'border-emerald-500/50 bg-emerald-500/5' : 
      'opacity-50'
    }`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isCompleted ? 'bg-emerald-500 text-white' :
            isActive ? 'bg-violet-500 text-white' :
            'bg-muted text-muted-foreground'
          }`}>
            {isCompleted ? <Check className="w-4 h-4" /> : step.icon}
          </div>
          <div>
            <h3 className="font-semibold">{step.title}</h3>
            <p className="text-xs text-muted-foreground">{step.description}</p>
          </div>
          {isCompleted && (
            <Badge className="ml-auto bg-emerald-500">Approved</Badge>
          )}
          {isActive && (
            <Badge className="ml-auto bg-violet-500">Current Step</Badge>
          )}
        </div>
        {(isActive || isCompleted) && children}
      </CardContent>
    </Card>
  );
};

// Message Variant Selector
const MessageVariantSelector = ({ variants, selectedId, onSelect, onCustomize }) => {
  const [customizing, setCustomizing] = useState(null);
  const [customContent, setCustomContent] = useState('');

  const handleCustomize = (variant) => {
    setCustomizing(variant.id || variant.variant || 'custom');
    setCustomContent(variant.content || variant.message || '');
  };

  const saveCustomization = () => {
    if (customizing && customContent.trim()) {
      onCustomize(customizing, customContent);
      setCustomizing(null);
    }
  };

  if (!variants || variants.length === 0) {
    return <p className="text-sm text-muted-foreground">No message variants available</p>;
  }

  return (
    <div className="space-y-3">
      {variants.map((variant, idx) => {
        const variantId = variant.id || variant.variant || `v${idx + 1}`;
        const channel = variant.channel || 'sms';
        const content = variant.content || variant.message || '';
        const isSelected = selectedId === variantId;
        
        return (
          <Card 
            key={variantId}
            className={`cursor-pointer transition-all ${
              isSelected ? 'border-violet-500 bg-violet-500/5' : 'hover:border-violet-500/50'
            }`}
            onClick={() => !customizing && onSelect(variantId)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {channel === 'sms' ? <MessageSquare className="w-3 h-3 mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
                    {(channel || 'SMS').toUpperCase()}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {variant.generated_by === 'ai' ? '🤖 AI Generated' : '✏️ Custom'}
                  </Badge>
                  {idx === 0 && <Badge className="bg-amber-500 text-xs">Recommended</Badge>}
                </div>
                {isSelected && (
                  <CheckCircle2 className="w-5 h-5 text-violet-500" />
                )}
              </div>
              
              {customizing === variantId ? (
                <div className="space-y-2">
                  <Textarea 
                    value={customContent}
                    onChange={(e) => setCustomContent(e.target.value)}
                    className="min-h-[100px]"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveCustomization}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setCustomizing(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{content}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">
                      {content.length} characters
                      {channel === 'sms' && ` (${Math.ceil(content.length / 160)} SMS)`}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="ml-auto gap-1"
                      onClick={(e) => { e.stopPropagation(); handleCustomize(variant); }}
                    >
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Approval History Timeline
const ApprovalHistory = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">No approval history yet</p>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((record, idx) => (
        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            record.action === 'approve' ? 'bg-emerald-500/20 text-emerald-500' :
            record.action === 'reject' ? 'bg-red-500/20 text-red-500' :
            'bg-amber-500/20 text-amber-500'
          }`}>
            {record.action === 'approve' ? <Check className="w-4 h-4" /> :
             record.action === 'reject' ? <X className="w-4 h-4" /> :
             <Edit3 className="w-4 h-4" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <p className="font-medium text-sm">
                {record.step.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </p>
              <Badge variant={record.action === 'approve' ? 'default' : record.action === 'reject' ? 'destructive' : 'secondary'}>
                {record.action}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              By {record.approved_by_email} • {new Date(record.approved_at).toLocaleString('en-IN')}
            </p>
            {record.comments && (
              <p className="text-xs mt-1 italic">"{record.comments}"</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Campaign Approval Workflow Component
export default function CampaignApprovalWorkflow({ campaign, onClose, onComplete }) {
  const [selectedVariant, setSelectedVariant] = useState(campaign.selected_message?.id || null);
  const [confirmedCount, setConfirmedCount] = useState(campaign.confirmed_audience_size || campaign.estimated_audience_size || 0);
  const [approvalComments, setApprovalComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Determine current workflow state
  const approvalHistory = campaign.approval_history || [];
  const approvedSteps = approvalHistory.filter(a => a.action === 'approve').map(a => a.step);
  
  const isMessageReviewDone = approvedSteps.includes('message_review');
  const isAudienceConfirmationDone = approvedSteps.includes('audience_confirmation');
  const isFinalApprovalDone = approvedSteps.includes('final_approval');
  
  const currentStep = !isMessageReviewDone ? 'message_review' : 
                      !isAudienceConfirmationDone ? 'audience_confirmation' : 
                      !isFinalApprovalDone ? 'final_approval' : 'complete';

  const steps = [
    { id: 'message_review', title: 'Message Review', description: 'Select and approve message content', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'audience_confirmation', title: 'Audience Confirmation', description: 'Confirm target audience count', icon: <Users className="w-4 h-4" /> },
    { id: 'final_approval', title: 'Final Approval', description: 'Final sign-off before scheduling', icon: <Shield className="w-4 h-4" /> }
  ];

  // Select message variant
  const handleSelectVariant = async (variantId) => {
    setSelectedVariant(variantId);
    
    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/select-message`,
        { variant_id: variantId },
        { withCredentials: true }
      );
      toast.success('Message variant selected');
    } catch (err) {
      console.error('Select variant error:', err);
      toast.error('Failed to select message');
    }
  };

  // Customize message
  const handleCustomizeMessage = async (variantId, content) => {
    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/select-message`,
        { variant_id: variantId, custom_content: content },
        { withCredentials: true }
      );
      toast.success('Message customized and selected');
      setSelectedVariant(variantId);
    } catch (err) {
      console.error('Customize message error:', err);
      toast.error('Failed to customize message');
    }
  };

  // Confirm audience
  const handleConfirmAudience = async () => {
    if (!confirmedCount || confirmedCount < 1) {
      toast.error('Please enter a valid audience count');
      return;
    }

    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/confirm-audience`,
        { confirmed_count: parseInt(confirmedCount) },
        { withCredentials: true }
      );
      toast.success(`Audience count of ${parseInt(confirmedCount).toLocaleString()} confirmed`);
    } catch (err) {
      console.error('Confirm audience error:', err);
      toast.error('Failed to confirm audience');
    }
  };

  // Approve step
  const handleApproveStep = async (step) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/approve`,
        { 
          step, 
          action: 'approve',
          comments: approvalComments 
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setApprovalComments('');
      
      // Refresh campaign data
      if (response.data.campaign_status === 'approved') {
        toast.success('Campaign fully approved! Ready to schedule.', { duration: 5000 });
        onComplete?.();
      } else {
        // Force re-render by closing and reopening
        onClose?.();
      }
    } catch (err) {
      console.error('Approve step error:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setLoading(false);
    }
  };

  // Reject campaign
  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this campaign?')) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/approve`,
        { 
          step: currentStep, 
          action: 'reject',
          comments: approvalComments || 'Campaign rejected'
        },
        { withCredentials: true }
      );
      
      toast.success('Campaign rejected');
      onClose?.();
    } catch (err) {
      console.error('Reject error:', err);
      toast.error('Failed to reject campaign');
    } finally {
      setLoading(false);
    }
  };

  const progress = (approvedSteps.length / 3) * 100;

  return (
    <div className="space-y-6" data-testid="campaign-approval-workflow">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-xl font-bold">{campaign.name}</h2>
            <p className="text-sm text-muted-foreground">
              {campaign.insight_source ? `From AI Recommendation: ${campaign.insight_source.replace(/_/g, ' ')}` : 'Campaign Approval'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}>
            <History className="w-4 h-4 mr-2" />
            {showHistory ? 'Hide' : 'Show'} History
          </Button>
          {campaign.status !== 'approved' && (
            <Badge className={
              campaign.status === 'draft' ? 'bg-gray-500' :
              campaign.status === 'pending_approval' ? 'bg-amber-500' :
              campaign.status === 'rejected' ? 'bg-red-500' :
              'bg-emerald-500'
            }>
              {campaign.status?.replace(/_/g, ' ').toUpperCase()}
            </Badge>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>Approval Progress</span>
          <span className="font-medium">{Math.round(progress)}% Complete</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Approval History (Collapsible) */}
      {showHistory && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4" />
              Approval Audit Trail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ApprovalHistory history={approvalHistory} />
          </CardContent>
        </Card>
      )}

      {/* Campaign Summary */}
      <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/5 border-violet-500/20">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Target Segment</p>
              <p className="font-semibold">{campaign.audience?.segment_name || campaign.segment?.name || 'Custom'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Estimated Audience</p>
              <p className="font-semibold">{(campaign.estimated_audience_size || campaign.segment?.estimated_size || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Expected ROI</p>
              <p className="font-semibold text-emerald-500">{campaign.expected_roi?.roi_multiplier || '4-6x'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Urgency</p>
              <Badge className={
                campaign.urgency === 'high' ? 'bg-red-500' :
                campaign.urgency === 'medium' ? 'bg-amber-500' :
                'bg-blue-500'
              }>
                {campaign.urgency?.toUpperCase() || 'MEDIUM'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Steps */}
      <div className="space-y-4">
        {/* Step 1: Message Review */}
        <ApprovalStepCard 
          step={steps[0]} 
          isActive={currentStep === 'message_review'} 
          isCompleted={isMessageReviewDone}
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Review the AI-generated message variants and select the one to send:
            </p>
            <MessageVariantSelector 
              variants={campaign.message_variants || []}
              selectedId={selectedVariant}
              onSelect={handleSelectVariant}
              onCustomize={handleCustomizeMessage}
            />
            
            {currentStep === 'message_review' && (
              <div className="flex items-center gap-3 pt-4 border-t">
                <Textarea 
                  placeholder="Add approval comments (optional)..."
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  className="flex-1"
                />
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => handleApproveStep('message_review')}
                    disabled={!selectedVariant || loading}
                    className="gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Approve Message
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleReject} disabled={loading}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ApprovalStepCard>

        {/* Step 2: Audience Confirmation */}
        <ApprovalStepCard 
          step={steps[1]} 
          isActive={currentStep === 'audience_confirmation'} 
          isCompleted={isAudienceConfirmationDone}
        >
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">Estimated Audience</p>
                <p className="text-2xl font-bold">
                  {(campaign.estimated_audience_size || campaign.segment?.estimated_size || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Based on segment: {campaign.audience?.segment_name || campaign.segment?.name}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
                <p className="text-xs text-muted-foreground mb-1">Confirmed Audience Count</p>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    value={confirmedCount}
                    onChange={(e) => setConfirmedCount(e.target.value)}
                    className="text-xl font-bold"
                    disabled={isAudienceConfirmationDone}
                  />
                  {!isAudienceConfirmationDone && currentStep === 'audience_confirmation' && (
                    <Button onClick={handleConfirmAudience}>Confirm</Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Please verify the count before approval
                </p>
              </div>
            </div>
            
            {currentStep === 'audience_confirmation' && (
              <div className="flex items-center gap-3 pt-4 border-t">
                <Textarea 
                  placeholder="Add approval comments (optional)..."
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  className="flex-1"
                />
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => handleApproveStep('audience_confirmation')}
                    disabled={!confirmedCount || loading}
                    className="gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Approve Audience
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleReject} disabled={loading}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ApprovalStepCard>

        {/* Step 3: Final Approval */}
        <ApprovalStepCard 
          step={steps[2]} 
          isActive={currentStep === 'final_approval'} 
          isCompleted={isFinalApprovalDone}
        >
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-500" />
                Final Review Summary
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Selected Message:</p>
                  <p className="font-medium mt-1">
                    {campaign.selected_message?.channel?.toUpperCase() || 'SMS'} - Variant {selectedVariant?.toUpperCase() || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Confirmed Audience:</p>
                  <p className="font-medium mt-1">{parseInt(confirmedCount || 0).toLocaleString()} customers</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Campaign Type:</p>
                  <p className="font-medium mt-1">{campaign.type || 'Cohort'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Channels:</p>
                  <p className="font-medium mt-1">{(campaign.channels || ['sms']).join(', ').toUpperCase()}</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <strong>Important:</strong> By approving, you confirm that:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-6">
                <li>• Message content has been reviewed and is accurate</li>
                <li>• Target audience count of {parseInt(confirmedCount || 0).toLocaleString()} is correct</li>
                <li>• Campaign is ready for scheduling and execution</li>
              </ul>
            </div>
            
            {currentStep === 'final_approval' && (
              <div className="flex items-center gap-3 pt-4 border-t">
                <Textarea 
                  placeholder="Add final approval comments (optional)..."
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  className="flex-1"
                />
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => handleApproveStep('final_approval')}
                    disabled={loading}
                    className="gap-2 bg-emerald-500 hover:bg-emerald-600"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Final Approval
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleReject} disabled={loading}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ApprovalStepCard>
      </div>

      {/* Completed State */}
      {currentStep === 'complete' && (
        <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border-emerald-500/30">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-emerald-500 mb-2">Campaign Approved!</h3>
            <p className="text-muted-foreground mb-4">
              All approval steps completed. Your campaign is ready to schedule.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button className="gap-2">
                <Calendar className="w-4 h-4" />
                Schedule Campaign
              </Button>
              <Button variant="outline" onClick={onClose}>
                Back to Campaigns
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
